import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';

export function formatDateToCalendarICS(
  date: Date,
  isDateOnly = false,
): string {
  const utcDate = new Date(date);
  utcDate.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  const pattern = isDateOnly ? 'YYYYMMDD' : 'YYYYMMDDTHHmmss';
  return `${dayjs(date).format(pattern)}`;
}

/**
 * Takes date and time as separate objects and returns a new Date object that combines them.
 * @param {Date | null} date - Date object that keeps date.
 * @param {Date | null} time - Date object that keeps time.
 * @returns {Date | null} - Date object that combines date and time.
 */
function mergeDateAndTime(
  date?: Date | null,
  time?: Date | null,
): Date | null | undefined {
  if (!date && !time) {
    return null;
  }

  if (!date) {
    return time;
  }
  if (!time) {
    return date;
  }
  const dateTime = new Date(date);
  dateTime.setMinutes(time.getMinutes());
  dateTime.setHours(time.getHours());
  // eslint-disable-next-line consistent-return
  return dateTime;
}

export type BuildCalendarICSProps = {
  date?: Date | null;
  time?: Date | null;
  roomUrlLink: string;
  passcodeText?: string;
  helpText?: string;
  title: string;
};

export function buildCalendarICS(
  props: BuildCalendarICSProps,
): string | undefined {
  const startDate = mergeDateAndTime(props.date, props.time);
  if (!startDate) {
    return undefined;
  }

  const isFullDay = !Boolean(props.time);
  const EVENT_DURATION_IN_MS = 50 * 60000;
  const endDate = isFullDay
    ? undefined
    : new Date(startDate.getTime() + EVENT_DURATION_IN_MS);

  const calendarIcs = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CLASS:PUBLIC',
    'PRODID: Doxy.me',
    'BEGIN:VEVENT',
    `UID:${uuidv4()}`,
    'SEQUENCE:0',
    `DESCRIPTION:${[props.passcodeText, props.helpText]
      .filter(Boolean)
      .join('\\n\\n')}`,
    `LOCATION:${props.roomUrlLink}`,
    `DTSTAMP:${formatDateToCalendarICS(new Date())}`,
    `DTSTART:${formatDateToCalendarICS(startDate, isFullDay)}`,
    endDate && `DTEND:${formatDateToCalendarICS(endDate, isFullDay)}`,
    `SUMMARY:${props.title}`,
    'END:VEVENT',
    'METHOD:PUBLISH',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');
  // eslint-disable-next-line consistent-return
  return calendarIcs;
}
